'use client'

import { useEffect } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/Button'
import { AlertTriangle } from 'lucide-react'

// Next.js wraps every route segment under app/ in an error boundary backed by
// this file (the App Router convention: a nested route can override it with
// its own error.tsx, otherwise this one catches anything under it). Without
// this file, an uncaught render error anywhere in the app showed the raw
// browser-native "Application error: a client-side exception has occurred"
// page with no way back in — this replaces that with a page consistent with
// the rest of the site and a way to recover without a manual URL edit.
//
// This does NOT catch errors thrown by the root layout itself (providers,
// the <html>/<body> shell) — see global-error.tsx for that much rarer case.
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    // Surfaces in the browser console (and anywhere already scraping it) —
    // swap/add a real error-tracking call here (Sentry, etc.) if one gets
    // wired up later; this is the single place that would go.
    console.error('[CVCircuit] Unhandled error:', error)
  }, [error])

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center max-w-md mx-auto px-4">
        <AlertTriangle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h1>
        <p className="text-gray-600 mb-6">
          An unexpected error occurred. Your work up to this point should still be safe — try again, or head back to your dashboard.
        </p>
        <div className="flex items-center justify-center gap-3">
          <Button onClick={() => reset()}>Try again</Button>
          <Link href="/dashboard">
            <Button variant="outline">Go to Dashboard</Button>
          </Link>
        </div>
        {error.digest && (
          <p className="text-xs text-gray-400 mt-6">Reference: {error.digest}</p>
        )}
      </div>
    </div>
  )
}
